import { useState, useEffect, useCallback, useMemo } from 'react';
import { PropToolSettings } from '../../types/prop';
import { PropInstance, assetsApi } from '../../services/api';
import { Asset } from '../../types/asset';
import { useEditor } from '../../contexts/EditorContext';
import { loadHotbarPresets, validateHotbarPresets, autoFillHotbar, setActivePreset, HotbarPresets, HOTBAR_PRESET_COUNT } from '../../services/hotbarService';
import './PropPanel.css';

interface PropPanelProps {
  settings: PropToolSettings;
  onSettingsChange: (settings: PropToolSettings) => void;
  selectedPropInstance: PropInstance | null;
  /** Number of currently selected props (for multi-selection display) */
  selectedCount?: number;
  onUpdateProp: (updates: Partial<PropInstance>) => void;
  onDeleteProp: () => void;
  /** Map scale in pixels per meter */
  mapScale: number;
  /** Preloaded prop images for size calculation */
  propImages: Map<string, HTMLImageElement>;
}

/** Default size for props that don't have a defaultSizeMeters set */
const DEFAULT_PROP_SIZE_METERS = 1;

/**
 * Panel for prop placement tool settings
 * Shows hotbar (2x4 grid) for quick prop selection
 */
function PropPanel({
  settings,
  onSettingsChange,
  selectedPropInstance,
  selectedCount = 0,
  onUpdateProp,
  onDeleteProp,
  mapScale,
  propImages
}: PropPanelProps): JSX.Element {
  const { assetsVersion, refreshAssets } = useEditor();
  const [props, setProps] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [editingSizeFor, setEditingSizeFor] = useState<string | null>(null);
  const [editSizeValue, setEditSizeValue] = useState<string>('');
  const [propsPresets, setPropsPresets] = useState<HotbarPresets>({ presets: [], activePreset: 0 });

  /**
   * Fetches prop assets
   */
  useEffect(() => {
    const fetchProps = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await assetsApi.list('props');
        setProps(response.assets);

        // Validate and load hotbar presets (removes invalid asset references)
        const propNames = response.assets.map(a => a.name);
        const validPropNames = new Set(propNames);
        let presets = validateHotbarPresets('props', validPropNames);

        // Auto-fill first hotbar preset if completely empty
        if (presets.presets[0]?.slots.every(s => s === null) && propNames.length > 0) {
          autoFillHotbar('props', 0, propNames);
          presets = loadHotbarPresets('props', validPropNames);
        }

        setPropsPresets(presets);
      } catch (err) {
        console.error('Failed to fetch props:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProps();
  }, [assetsVersion]);

  /**
   * Get active hotbar props with their full data
   */
  const activePropsSlots = useMemo(() => {
    const activePreset = propsPresets.presets[propsPresets.activePreset];
    if (!activePreset) return [];
    return activePreset.slots.map(name => {
      if (!name) return null;
      return props.find(p => p.name === name) || null;
    });
  }, [propsPresets, props]);

  /**
   * Handle switching prop preset
   */
  const handlePresetChange = (presetIndex: number): void => {
    const newPresets = setActivePreset('props', presetIndex);
    setPropsPresets(newPresets);
  };

  /**
   * Calculate the scale factor needed to make a prop a certain size in meters
   */
  const calculateScaleForSize = useCallback((propName: string, sizeMeters: number): number => {
    const img = propImages.get(propName);
    if (!img) return 1;

    const largestDimension = Math.max(img.width, img.height);
    const targetSizePixels = sizeMeters * mapScale;
    return targetSizePixels / largestDimension;
  }, [propImages, mapScale]);

  /**
   * Get the size in meters for a prop asset
   */
  const getPropSizeMeters = useCallback((prop: Asset): number => {
    return prop.defaultSizeMeters ?? DEFAULT_PROP_SIZE_METERS;
  }, []);

  /**
   * Handle starting to edit size for a prop
   */
  const handleStartEditSize = useCallback((propName: string, currentSize: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSizeFor(propName);
    setEditSizeValue(currentSize.toString());
  }, []);

  /**
   * Handle saving the edited size
   */
  const handleSaveSize = useCallback(async () => {
    if (!editingSizeFor) return;

    const newSize = parseFloat(editSizeValue);
    if (isNaN(newSize) || newSize <= 0) {
      setEditingSizeFor(null);
      return;
    }

    try {
      await assetsApi.update('props', editingSizeFor, { defaultSizeMeters: newSize });
      setProps(prev => prev.map(p =>
        p.name === editingSizeFor ? { ...p, defaultSizeMeters: newSize } : p
      ));
      refreshAssets();
    } catch (err) {
      console.error('Failed to update prop size:', err);
    }

    setEditingSizeFor(null);
  }, [editingSizeFor, editSizeValue, refreshAssets]);

  /**
   * Handle selecting a prop (sets the scale based on defaultSizeMeters)
   */
  const handleSelectProp = useCallback((prop: Asset) => {
    const sizeMeters = getPropSizeMeters(prop);
    const scale = calculateScaleForSize(prop.name, sizeMeters);
    onSettingsChange({
      ...settings,
      selectedProp: prop.name,
      scale: scale
    });
  }, [settings, onSettingsChange, getPropSizeMeters, calculateScaleForSize]);

  if (isLoading) {
    return (
      <div className="prop-panel">
        <div className="prop-panel-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading props...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="prop-panel">
      {/* Selected Prop Instance Controls */}
      {selectedPropInstance && (
        <div className="prop-panel-section prop-panel-selected">
          <div className="prop-panel-section-title">
            {selectedCount > 1 ? `${selectedCount} Props Selected` : 'Selected Prop'}
            <button
              className="prop-panel-deselect"
              onClick={() => onUpdateProp({})}
              title="Deselect"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          {selectedCount === 1 && (
            <div className="prop-panel-selected-info">
              <div className="prop-panel-selected-preview">
                <img
                  src={assetsApi.getFileUrl('props', selectedPropInstance.propName)}
                  alt={selectedPropInstance.propName}
                />
              </div>
              <div className="prop-panel-selected-name">{selectedPropInstance.propName}</div>
            </div>
          )}
          <div className="prop-panel-actions">
            <button
              className="prop-panel-action-btn prop-panel-action-btn--danger"
              onClick={onDeleteProp}
              title={selectedCount > 1 ? `Delete ${selectedCount} Props` : 'Delete Prop'}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
          <div className="prop-panel-section-divider"></div>
          <div className="prop-panel-section-subtitle">Change Type {selectedCount > 1 ? '(All)' : ''}</div>
          <div className="prop-panel-grid prop-panel-grid--small">
            {props.map((prop) => (
              <button
                key={prop.name}
                className={`prop-panel-asset ${selectedPropInstance.propName === prop.name ? 'prop-panel-asset--selected' : ''}`}
                onClick={() => onUpdateProp({ propName: prop.name })}
                title={prop.label}
              >
                <img
                  src={assetsApi.getFileUrl('props', prop.name)}
                  alt={prop.label}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Prop Selection Hotbar for New Props */}
      {!selectedPropInstance && (
        <>
          <div className="prop-panel-section">
            <div className="prop-panel-section-title">
              <span>Props</span>
              <div className="prop-panel-preset-switcher">
                {Array.from({ length: HOTBAR_PRESET_COUNT }).map((_, i) => (
                  <button
                    key={i}
                    className={`prop-panel-preset-btn ${propsPresets.activePreset === i ? 'prop-panel-preset-btn--active' : ''}`}
                    onClick={() => handlePresetChange(i)}
                    title={`Preset ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
            {props.length === 0 ? (
              <div className="prop-panel-empty">No props available</div>
            ) : (
              <div className="prop-panel-hotbar">
                {activePropsSlots.map((prop, index) => {
                  const sizeMeters = prop ? getPropSizeMeters(prop) : 0;
                  const isEditing = prop && editingSizeFor === prop.name;

                  return (
                    <div key={index} className="prop-panel-hotbar-wrapper">
                      <button
                        className={`prop-panel-hotbar-slot ${prop && settings.selectedProp === prop.name ? 'prop-panel-hotbar-slot--selected' : ''} ${!prop ? 'prop-panel-hotbar-slot--empty' : ''}`}
                        onClick={() => prop && handleSelectProp(prop)}
                        title={prop?.label || 'Empty slot'}
                        disabled={!prop}
                      >
                        {prop ? (
                          <img
                            src={assetsApi.getFileUrl('props', prop.name)}
                            alt={prop.label}
                          />
                        ) : (
                          <span className="prop-panel-hotbar-slot-empty-icon"></span>
                        )}
                      </button>
                      {prop && (
                        isEditing ? (
                          <input
                            type="number"
                            className="prop-panel-size-input"
                            value={editSizeValue}
                            onChange={(e) => setEditSizeValue(e.target.value)}
                            onBlur={handleSaveSize}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveSize();
                              if (e.key === 'Escape') setEditingSizeFor(null);
                            }}
                            step="0.1"
                            min="0.01"
                            autoFocus
                          />
                        ) : (
                          <button
                            className="prop-panel-size-label"
                            onClick={(e) => handleStartEditSize(prop.name, sizeMeters, e)}
                            title="Click to edit size"
                          >
                            {sizeMeters >= 1 ? `${sizeMeters}m` : `${Math.round(sizeMeters * 100)}cm`}
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Help Text */}
      <div className="prop-panel-help">
        <div className="prop-panel-help-item">
          <kbd>Click</kbd> Place prop
        </div>
        <div className="prop-panel-help-item">
          <kbd>Ctrl + Click</kbd> Force place (ignore selection)
        </div>
        <div className="prop-panel-help-item">
          <kbd>Drag</kbd> Selection box
        </div>
        <div className="prop-panel-help-item">
          <kbd>Shift + Click</kbd> Add/remove from selection
        </div>
        <div className="prop-panel-help-item">
          <kbd>Shift + Drag</kbd> Snap to grid
        </div>
        <div className="prop-panel-help-item">
          <kbd>Ctrl + Drag</kbd> Non-uniform scale
        </div>
        <div className="prop-panel-help-item">
          <kbd>Scale past center</kbd> Flip/mirror
        </div>
        <div className="prop-panel-help-item">
          <kbd>Del</kbd> Delete selected
        </div>
      </div>
    </div>
  );
}

export default PropPanel;
